<template>
  <v-container fluid class="ma-0 pa-0">
    <v-row class="ma-0 pa-0 mt-4">
      <v-col cols="12" class="ma-0 pa-0">
        <v-card color="#eeeeee" class="pt-1">
          <v-card-title class="primary--text pl-3 py-2 flex-wrap">
            <v-btn
              text
              color="primary"
              class="title"
              @click="pickerOpen = true"
            >
              <v-icon left dark large class="pr-2">add_circle</v-icon>
              Variables
            </v-btn>

            <v-tooltip bottom color="primary">
              <template v-slot:activator="{ on: tooltip }">
                <span v-on="{ ...tooltip }" @click="pickerOpen = true">
                  <v-chip label color="primary" class="white--text title mr-2">
                    {{ selectedVariables.length }}
                  </v-chip>
                  <span class="black--text text-body-1">
                    of {{ availableVariables.length }}
                  </span>
                  <span
                    v-if="selectedVisitCount > 0"
                    class="black--text text-body-1 ml-1"
                  >
                    + {{ selectedVisitCount }}
                  </span>
                </span>
              </template>
              <span class="subtitle-1">
                {{ selectedVariables.length }} of
                {{ availableVariables.length }} continuous variable{{
                  availableVariables.length === 1 ? '' : 's'
                }}
                selected<template v-if="selectedVisitCount > 0"
                  >, plus {{ selectedVisitCount }} last-visit value{{
                    selectedVisitCount === 1 ? '' : 's'
                  }}
                  or change{{ selectedVisitCount === 1 ? '' : 's' }}</template
                >
              </span>
            </v-tooltip>

            <v-divider vertical class="ml-4 mr-4"></v-divider>

            <v-btn
              text
              color="primary"
              class="title"
              :disabled="availableCovariates.length === 0"
              @click="covariatePickerOpen = true"
            >
              <v-icon left dark large class="pr-2">add_circle</v-icon>
              Adjust for
            </v-btn>

            <v-tooltip bottom color="primary">
              <template v-slot:activator="{ on: tooltip }">
                <span v-on="{ ...tooltip }" @click="openCovariatePicker">
                  <v-chip label color="primary" class="white--text title mr-2">
                    {{ covariateVariables.length }}
                  </v-chip>
                  <span class="black--text text-body-1">
                    of {{ availableCovariates.length }}
                  </span>
                </span>
              </template>
              <span v-if="availableCovariates.length === 0" class="subtitle-1">
                No subject-level or demographic variables to adjust for
              </span>
              <span v-else class="subtitle-1">
                {{ covariateVariables.length }} of
                {{ availableCovariates.length }} subject-level or demographic
                variable{{
                  availableCovariates.length === 1 ? '' : 's'
                }}
                adjusted for
              </span>
            </v-tooltip>

            <v-spacer />

            <span class="black--text text-body-2 mr-4">
              {{ contextLabel }}
            </span>

            <v-btn
              text
              small
              color="primary"
              :disabled="
                selectedIds.length === 0 && covariateVariables.length === 0
              "
              @click="clearSelection"
            >
              clear
            </v-btn>

            <v-btn text small color="primary" @click="resetSelection">
              reset
            </v-btn>
          </v-card-title>
        </v-card>
      </v-col>
    </v-row>

    <v-row class="ma-0 pa-0 mt-3">
      <v-col cols="12" class="ma-0 pa-0">
        <v-card class="ma-0 pa-3">
          <div class="d-flex align-baseline mb-1">
            <span class="text-h6">Correlation matrix</span>
            <v-spacer />
            <span class="cs-caption">
              {{
                visitsInNames
                  ? 'continuous variables at the visits shown in their names'
                  : 'continuous variables at baseline'
              }}
              &mdash; one value per subject
            </span>
          </div>

          <div
            v-if="availableVariables.length < 2"
            :class="emptyStateClass"
            align="center"
          >
            NOT ENOUGH CONTINUOUS VARIABLES
          </div>

          <div
            v-else-if="matrixVars.length < 2"
            :class="emptyStateClass"
            align="center"
          >
            SELECT AT LEAST TWO VARIABLES
          </div>

          <template v-else>
            <div class="d-flex align-center flex-wrap mt-2 mb-3">
              <span class="cs-label mr-2">METHOD</span>
              <v-btn-toggle
                :value="method"
                mandatory
                dense
                color="primary"
                class="mr-6"
                @change="setMethod"
              >
                <v-btn small :value="METHOD_PEARSON">Pearson</v-btn>
                <v-btn small :value="METHOD_SPEARMAN">Spearman</v-btn>
              </v-btn-toggle>

              <span class="cs-label mr-2">ORDER</span>
              <v-btn-toggle
                :value="sort"
                mandatory
                dense
                color="primary"
                @change="setSort"
              >
                <v-btn small :value="SORT_SELECTION">variable list</v-btn>
                <v-btn small :value="SORT_STRENGTH">by strength</v-btn>
              </v-btn-toggle>
            </div>

            <!-- chosen in the toolbar; kept in view here, beside the matrix
                 they change, so an adjustment is never forgotten -->
            <div
              v-if="covariateVariables.length > 0"
              class="d-flex align-center flex-wrap mb-3"
            >
              <span class="cs-label mr-2">ADJUSTED FOR</span>
              <v-chip
                v-for="v in covariateVariables"
                :key="'cs-cov-' + v.uid"
                small
                close
                class="mr-1 my-1"
                @click:close="removeCovariate(v.uid)"
              >
                {{ varName(v) }}
              </v-chip>
            </div>

            <correlation-heatmap
              :names="orderedVariables.map(varName)"
              :cells="orderedCells"
              :method="method"
              :adjusted-for="covariateNames"
              @select="openDetail"
            />

            <div v-if="covariateNames.length > 0" class="cs-caption mt-1">
              Partial correlations: each cell is computed on the subjects with a
              value for both variables and for every covariate. Hover a cell for
              the unadjusted value on those same subjects.
            </div>

            <div class="cs-caption mt-1">
              <!-- Exploratory: p-values in the detail view are not corrected for -->
              <!-- multiple comparisons. -->
            </div>
          </template>
        </v-card>
      </v-col>
    </v-row>

    <variable-picker-dialog
      v-model="pickerOpen"
      title="Choose Variables to Correlate"
      :variables="availableVariables"
      :selected-ids="selectedIds"
      :last-ids="lastIds"
      :delta-ids="deltaIds"
      :visit-uids="visitUids"
      @update:selectedIds="setSelectedIds"
      @update:lastIds="setLastIds"
      @update:deltaIds="setDeltaIds"
    />

    <variable-picker-dialog
      v-model="covariatePickerOpen"
      title="Choose Variables to Adjust For"
      :variables="availableCovariates"
      :selected-ids="covariateVariables.map(v => v.uid)"
      @update:selectedIds="setCovariates"
    />

    <pair-detail-dialog
      v-model="detailOpen"
      :detail="detail"
      :stats="detailStats"
      :loading="detailLoading"
      :failed="detailFailed"
      :stratify-options="stratifyOptions"
      :stratify-uid="stratifyUid"
      :covariate-names="covariateNames"
      @update:stratifyUid="setStratify"
    />
  </v-container>
</template>

<script>
import axios from 'axios';
import { mapState } from 'vuex';
import { state } from '@/store/modules/datasetSummary/types';
import {
  DATA_CATEGORY_CATEGORICAL,
  MAX_SPLIT_LEVELS,
  MIN_GROUP_SIZE,
  baselineValuesBySubject,
  changeValuesBySubject,
  lastValuesBySubject,
  deriveGroups,
  hasVisitWindow,
  isDashboardEligible,
  isPredictor,
  isSplitEligible,
  predictorSortKey,
  reconcileSelection,
  variableKey,
} from '@/utils/dashboard';
import {
  METHOD_PEARSON,
  METHOD_SPEARMAN,
  SORT_SELECTION,
  SORT_STRENGTH,
  adjustedPairs,
  adjustedPoints,
  buildCorrelationPayload,
  correlationMatrix,
  covariateSpec,
  excludedCount,
  isCorrelationEligible,
  isCovariateEligible,
  markCoupled,
  matrixVariables,
  visitMeasures,
  pairExportFilename,
  permuteMatrix,
  selectDefaultCorrelationVariables,
  strengthOrder,
  stratifiedPairs,
} from '@/utils/correlation';
import { readStoredJson, writeStoredJson } from '@/utils/helpers';
import VariablePickerDialog from '@/components/DatasetSummary/DataDashboard/VariablePickerDialog.vue';
import CorrelationHeatmap from '@/components/DatasetSummary/Correlations/CorrelationHeatmap.vue';
import PairDetailDialog from '@/components/DatasetSummary/Correlations/PairDetailDialog.vue';

// localStorage key prefix; scoped per dataset, as in DataDashboard
const SELECTION_KEY_PREFIX = 'correlation_selection';

export default {
  name: 'CorrelationSummary',
  components: {
    CorrelationHeatmap,
    PairDetailDialog,
    VariablePickerDialog,
  },
  data() {
    return {
      METHOD_PEARSON,
      METHOD_SPEARMAN,
      SORT_SELECTION,
      SORT_STRENGTH,
      selectedIds: [],
      // base uids whose first-to-last change is in the matrix; opt-in only
      // uids also wanted at their last visit, and as their first-to-last
      // change; opt-in only
      lastIds: [],
      deltaIds: [],
      method: METHOD_PEARSON,
      sort: SORT_SELECTION,
      stratifyUid: null,
      // uids of the predictors every correlation is adjusted for
      covariateIds: [],
      pickerOpen: false,
      covariatePickerOpen: false,
      detailOpen: false,
      // {x, y} variables of the open pair; x is the matrix column
      detailPair: null,
      detailLoading: false,
      detailFailed: false,
      // pair|stratum|covariates -> compute-correlation response
      statsCache: {},
      // discards responses from superseded requests, as in OutcomeByPredictor
      requestNum: 0,
    };
  },
  computed: {
    ...mapState('datasetSummary', {
      dataset: state.DATASET,
      collection: state.COLLECTION,
      isCollection: state.IS_COLLECTION,
      variables: state.VARIABLES,
      variableData: state.VARIABLE_DATA,
      visitVariable: state.VISIT_VARIABLE,
      useLongScaleNames: state.USE_LONG_SCALE_NAMES,
    }),
    emptyStateClass() {
      return 'display-1 primary--text text--lighten-5 pt-5 mt-5 pb-5';
    },
    subject() {
      return this.isCollection ? this.collection : this.dataset;
    },
    numSubjects() {
      return this.subject ? this.subject.num_subjects : 0;
    },
    longitudinal() {
      return !!(this.subject && this.subject.longitudinal);
    },
    storageKey() {
      if (!this.subject) return null;
      const scope = this.isCollection ? 'collection' : 'study';
      return `${SELECTION_KEY_PREFIX}-${scope}-${this.subject.id}`;
    },
    contextLabel() {
      let shape = 'cross-sectional';
      if (this.longitudinal) {
        shape = this.visitsInNames
          ? "longitudinal · each variable's first & last visit"
          : 'longitudinal · values at baseline';
      }
      return `${this.numSubjects} subjects · ${shape}`;
    },
    // `uid` attached once, as in DataDashboard, since a bare `id` collides
    // between subject and observation variables
    dashboardVariables() {
      return this.variables
        .filter(isDashboardEligible)
        .map(v => ({ ...v, uid: variableKey(v) }));
    },
    availableVariables() {
      return this.dashboardVariables.filter(isCorrelationEligible);
    },
    // available-list order rather than click order
    selectedVariables() {
      const wanted = new Set(this.selectedIds);
      return this.availableVariables.filter(v => wanted.has(v.uid));
    },
    // uid -> its First Visit, Last Visit, and Change measures, for each
    // variable with a saved first and last visit; only collections save them,
    // so studies offer none
    measuresByUid() {
      const measures = new Map();
      this.availableVariables.forEach(v => {
        if (hasVisitWindow(v, this.visitVariable, this.longitudinal)) {
          measures.set(v.uid, visitMeasures(v, this.visitVariable));
        }
      });
      return measures;
    },
    visitUids() {
      return Array.from(this.measuresByUid.keys());
    },
    // stored measures can go stale like any stored selection
    selectedVisitCount() {
      return [...this.lastIds, ...this.deltaIds].filter(id =>
        this.measuresByUid.has(id)
      ).length;
    },
    // what the matrix is built over: each variable's selected measures, in
    // First Visit, Last Visit, Change order
    matrixVars() {
      return matrixVariables(
        this.availableVariables,
        {
          firstIds: this.selectedIds,
          lastIds: this.lastIds,
          deltaIds: this.deltaIds,
        },
        this.measuresByUid
      );
    },
    // whether any name carries its visit, so captions point there rather than
    // saying "at baseline"
    visitsInNames() {
      return this.matrixVars.some(v => v.measure);
    },
    baseCells() {
      if (!this.variableData || this.matrixVars.length < 2) return [];
      const covariateUids = new Set(this.covariateVariables.map(v => v.uid));
      const covariateIndices = new Set();
      this.matrixVars.forEach((v, i) => {
        if (covariateUids.has(v.uid)) covariateIndices.add(i);
      });

      const cells = correlationMatrix(
        this.matrixVars.map(v => this.valuesFor(v)),
        this.method,
        { covariates: this.covariateSpecs, covariateIndices }
      );
      return markCoupled(cells, this.matrixVars);
    },
    // age, sex, race, and ethnicity first, as in the dashboard's predictors
    availableCovariates() {
      return this.dashboardVariables
        .filter(isCovariateEligible)
        .sort((a, b) => predictorSortKey(a) - predictorSortKey(b));
    },
    // stored covariates can go stale like any stored selection, so only those
    // still offered apply; kept in list order for stable labels and keys
    covariateVariables() {
      const wanted = new Set(this.covariateIds);
      return this.availableCovariates.filter(v => wanted.has(v.uid));
    },
    covariateNames() {
      return this.covariateVariables.map(this.varName);
    },
    covariateSpecs() {
      if (!this.variableData) return [];
      return this.covariateVariables.map(v =>
        covariateSpec(v, this.baselineFor(v))
      );
    },
    order() {
      const identity = this.matrixVars.map((v, i) => i);
      if (this.sort !== SORT_STRENGTH || this.baseCells.length === 0) {
        return identity;
      }
      return strengthOrder(this.baseCells);
    },
    orderedVariables() {
      return this.order.map(i => this.matrixVars[i]);
    },
    orderedCells() {
      return permuteMatrix(this.baseCells, this.order);
    },
    // every categorical predictor in the dataset, not just a selection: this
    // tab has no predictor picker, and strata are chosen per pair
    stratifyOptions() {
      if (!this.variableData) return [];
      return this.dashboardVariables
        .filter(
          v =>
            isPredictor(v) &&
            v.data_category === DATA_CATEGORY_CATEGORICAL &&
            isSplitEligible(v, this.derivedFor(v).levels.length)
        )
        .sort((a, b) => predictorSortKey(a) - predictorSortKey(b))
        .map(v => ({ value: v.uid, text: this.varName(v) }));
    },
    // a stored stratum can go stale like any stored selection, so it only
    // applies while it is still offered
    stratifyVariable() {
      if (!this.stratifyOptions.some(o => o.value === this.stratifyUid)) {
        return null;
      }
      return this.dashboardVariables.find(v => v.uid === this.stratifyUid);
    },
    detail() {
      if (!this.detailPair) return null;
      const { x, y } = this.detailPair;
      const xValues = this.valuesFor(x);
      const yValues = this.valuesFor(y);
      // with covariates, every view of the pair uses the subjects complete for
      // x, y, and all covariates, so observed and adjusted compare like with
      // like
      const paired = adjustedPairs(xValues, yValues, this.covariateSpecs);
      const adjusting = this.covariateSpecs.length > 0;

      const detail = {
        key: this.statsKey(),
        // identifies the pair alone; `key` also changes with the stratum
        pairKey: `${x.uid}|${y.uid}`,
        xName: this.varName(x),
        yName: this.varName(y),
        exportFilename: this.exportFilename(x, y, null),
        paired,
        adjusted: adjusting ? adjustedPoints(paired) : null,
        excluded: excludedCount(xValues, yValues),
        dropped: paired.dropped,
        strata: null,
        strataAdjustedFor: [],
        stratifyName: '',
        disclosure: '',
        visitsInNames: !!(x.measure || y.measure),
      };

      const strat = this.stratifyVariable;
      if (!strat) return detail;

      // within a stratum the stratifying variable is constant, so it is left
      // out of that stratum's adjustment
      const derived = this.derivedFor(strat);
      const within = this.covariateVariables.filter(v => v.uid !== strat.uid);
      const strata = stratifiedPairs(
        derived,
        xValues,
        yValues,
        within.map(v => covariateSpec(v, this.baselineFor(v)))
      );

      return {
        ...detail,
        // stratify, then adjust: each stratum's adjusted points use its own
        // covariate coding, as its partial r and fitted line do
        strata: strata.map(s => ({
          ...s,
          adjusted: adjusting ? adjustedPoints(s) : null,
        })),
        stratifyName: this.varName(strat),
        strataAdjustedFor: within.map(this.varName),
        exportFilename: this.exportFilename(x, y, strat),
        disclosure: this.buildDisclosure(derived),
      };
    },
    detailStats() {
      if (!this.detail) return null;
      return this.statsCache[this.detail.key] || null;
    },
  },
  watch: {
    // SET_VARIABLES is committed only on fetch, so this fires when the view
    // loads a different dataset while the tab stays mounted
    variables() {
      this.restoreSelection();
    },
  },
  created() {
    // memo tables, deliberately not reactive; each entry records the inputs it
    // was built from so a new dataset invalidates it
    this.baselineCache = new Map();
    this.lastCache = new Map();
    this.deltaCache = new Map();
    this.derivedCache = new Map();
    this.restoreSelection();
  },
  methods: {
    varName(v) {
      if (!v) return '';
      return this.useLongScaleNames ? v.label : v.abbreviation;
    },
    memo(cache, key, build) {
      const cached = cache.get(key);
      if (
        cached &&
        cached.source === this.variableData &&
        cached.visitVariable === this.visitVariable &&
        cached.longitudinal === this.longitudinal
      ) {
        return cached.value;
      }

      const value = build();
      cache.set(key, {
        source: this.variableData,
        visitVariable: this.visitVariable,
        longitudinal: this.longitudinal,
        value,
      });
      return value;
    },
    baselineFor(v) {
      return this.memo(this.baselineCache, v.uid, () =>
        baselineValuesBySubject(this.variableData, v, {
          longitudinal: this.longitudinal,
          visitVariable: this.visitVariable,
        })
      );
    },
    // keyed by window as well, so a changed window is never served stale
    changeFor(v) {
      return this.memo(this.deltaCache, `${v.uid}|${v.window}`, () =>
        changeValuesBySubject(this.variableData, v, {
          longitudinal: this.longitudinal,
          visitVariable: this.visitVariable,
        })
      );
    },
    // keyed by uid and name, which carries the visit
    lastFor(v) {
      return this.memo(this.lastCache, `${v.uid}|${v.abbreviation}`, () =>
        lastValuesBySubject(this.variableData, v, {
          longitudinal: this.longitudinal,
          visitVariable: this.visitVariable,
        })
      );
    },
    // a matrix variable's values: at its last visit, its change, or at
    // baseline (its first visit, when it has saved visits)
    valuesFor(v) {
      if (v.measure === 'last') return this.lastFor(v);
      if (v.measure === 'delta') return this.changeFor(v);
      return this.baselineFor(v);
    },
    derivedFor(v) {
      return this.memo(this.derivedCache, v.uid, () =>
        deriveGroups(this.baselineFor(v))
      );
    },
    // abbreviations rather than the display names, which can be long labels
    exportFilename(x, y, strat) {
      return pairExportFilename(
        this.isCollection ? 'collection' : 'study',
        this.subject ? this.subject.id : '',
        this.fileStem(x),
        this.fileStem(y),
        strat ? strat.abbreviation : null
      );
    },
    // spelled out, since the Δ itself does not survive the filename cleanup
    fileStem(v) {
      return v.delta
        ? `${v.baseAbbreviation} change ${v.window}`
        : v.abbreviation;
    },
    buildDisclosure(derived) {
      if (derived.dropped.length === 0) return '';
      const pct = Math.round(derived.coverage * 100);
      return (
        `showing ${derived.levels.length} of ${derived.totalLevels} levels ` +
        `(${pct}% of subjects with a value). ` +
        `${derived.dropped.length} hidden: fewer than ${MIN_GROUP_SIZE} ` +
        `subjects, or beyond the ${MAX_SPLIT_LEVELS}-level limit. ` +
        `Their subjects are drawn in grey.`
      );
    },
    statsKey() {
      if (!this.detailPair) return null;
      const { x, y } = this.detailPair;
      const strat = this.stratifyVariable;
      const covariates = this.covariateVariables.map(v => v.uid).join(',');
      return `${x.uid}|${y.uid}|${strat ? strat.uid : ''}|${covariates}`;
    },
    openDetail({ row, col }) {
      this.detailPair = {
        x: this.orderedVariables[col],
        y: this.orderedVariables[row],
      };
      this.detailOpen = true;
      this.fetchStats();
    },
    setStratify(uid) {
      this.stratifyUid = uid;
      this.persistSelection();
      if (this.detailOpen) this.fetchStats();
    },
    async fetchStats() {
      const detail = this.detail;
      this.detailFailed = false;
      if (!detail || this.statsCache[detail.key]) return;

      this.requestNum += 1;
      const reqNum = this.requestNum;
      this.detailLoading = true;

      try {
        const { data } = await axios.post(
          '/api/compute-correlation',
          buildCorrelationPayload(detail.paired, detail.strata || [])
        );
        if (reqNum !== this.requestNum) return;
        this.$set(this.statsCache, detail.key, data);
      } catch (err) {
        // the scatter is already on screen, so a failure degrades the
        // statistics rather than the whole dialog
        if (reqNum === this.requestNum) this.detailFailed = true;
      } finally {
        if (reqNum === this.requestNum) this.detailLoading = false;
      }
    },
    setMethod(method) {
      this.method = method;
      this.persistSelection();
    },
    setCovariates(ids) {
      this.covariateIds = ids;
      this.persistSelection();
    },
    removeCovariate(uid) {
      this.setCovariates(this.covariateIds.filter(id => id !== uid));
    },
    // the count chip is a second launcher, as on the Variables side, but has
    // nothing to open when the dataset offers no covariates
    openCovariatePicker() {
      if (this.availableCovariates.length > 0) this.covariatePickerOpen = true;
    },
    setSort(sort) {
      this.sort = sort;
      this.persistSelection();
    },
    setSelectedIds(ids) {
      this.selectedIds = ids;
      this.persistSelection();
    },
    setLastIds(ids) {
      this.lastIds = ids;
      this.persistSelection();
    },
    setDeltaIds(ids) {
      this.deltaIds = ids;
      this.persistSelection();
    },
    // both buttons cover covariates and visit measures too, so neither leaves
    // something behind that the user did not expect
    clearSelection() {
      this.selectedIds = [];
      this.lastIds = [];
      this.deltaIds = [];
      this.covariateIds = [];
      this.persistSelection();
    },
    resetSelection() {
      this.applyDefaults();
      this.lastIds = [];
      this.deltaIds = [];
      this.covariateIds = [];
      this.persistSelection();
    },
    applyDefaults() {
      this.selectedIds = selectDefaultCorrelationVariables(
        this.availableVariables
      ).map(v => v.uid);
    },
    // as in DataDashboard: restoring never writes, so an untouched dataset
    // keeps getting fresh defaults
    restoreSelection() {
      // results are keyed by variable uid, which means nothing in another
      // dataset
      this.statsCache = {};
      this.detailPair = null;
      this.detailOpen = false;

      const stored =
        (this.storageKey ? readStoredJson(this.storageKey) : null) || {};
      this.applyDefaults();

      const ids = reconcileSelection(stored.variables, this.availableVariables);
      if (ids !== null) this.selectedIds = ids;
      // stale entries are harmless: matrixVars skips any without saved visits
      this.lastIds = Array.isArray(stored.lasts) ? stored.lasts : [];
      this.deltaIds = Array.isArray(stored.deltas) ? stored.deltas : [];
      this.method =
        stored.method === METHOD_SPEARMAN ? METHOD_SPEARMAN : METHOD_PEARSON;
      this.sort =
        stored.sort === SORT_STRENGTH ? SORT_STRENGTH : SORT_SELECTION;
      this.stratifyUid = stored.stratify || null;
      this.covariateIds = Array.isArray(stored.covariates)
        ? stored.covariates
        : [];
    },
    persistSelection() {
      if (!this.storageKey) return;
      writeStoredJson(this.storageKey, {
        variables: this.selectedIds,
        lasts: this.lastIds,
        deltas: this.deltaIds,
        method: this.method,
        sort: this.sort,
        stratify: this.stratifyUid,
        covariates: this.covariateIds,
      });
    },
  },
};
</script>

<style scoped>
.cs-caption {
  font-size: 12px;
  color: #777;
}
.cs-label {
  font-size: 11px;
  color: #777;
  letter-spacing: 0.6px;
  white-space: nowrap;
}
</style>
